import { Button, Accordion, Modal as BootstrapModal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from 'react-router-dom';
import { useCallback, useMemo, useState, useRef } from "react";
import { Form, Formik } from "formik";

import { sendNotification } from '../../../services/notifications.js';
import NotificationTemplate from "../../../services/notificationTemplate";
import PubSub from "../../../services/PubSub";
import { useNotifications } from '../NotificationsContext.js';
import Icon from '../../controls/Icon.jsx';
import List from '../../controls/List.jsx';
import SearchBar from "../../controls/SearchBar.jsx";
import GenericCheckBox from "../../controls/GenericCheckbox.jsx";
import NotificationTemplateLayout from "./NotificationTemplateLayout.jsx";

const SendNotificationModal = ({
    show,
    permissions,
    templatePermissions,
    templateNotification,
    onClose
}) => {
    const ref = useRef();
    const { id, objectSubTypeId } = useParams();
    const [translate] = useTranslation();

    const { userIds } = useNotifications();

    const [users] = useState(userIds);
    const [selectedUsersSearchPrompt, setSelectedUsersSearchPrompt] = useState("");
    const [unselectedUsersSearchPrompt, setUnselectedUsersSearchPrompt] = useState("");
    const [saveAsTemplate, setSaveAsTemplate] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [preselectedUsers, setPreselectedUsers] = useState([]);
    const [unselectedUsers, setUnselectedUsers] = useState([...users]);
    const [preunselectedUsers, setPreunselectedUsers] = useState([]);
    const [keepOpen, setKeepOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const updateUserList = useCallback((previousList, eventKey) => {
        const index = previousList.indexOf(eventKey);

        if (index > -1) {
            previousList.splice(index, 1);

            return [...previousList];
        }

        return [...previousList, eventKey];
    }, []);

    const selectUsers = useCallback(() => {
        setPreselectedUsers((currentPreselectedUsers) => {
            setSelectedUsers((previousList) => {
                const result = [...previousList, ...currentPreselectedUsers];

                setUnselectedUsers(users.filter((user) => !result.includes(user)));

                return result;
            });

            return [];
        });
    }, [users]);

    const selectAllUsers = useCallback(() => {
        setPreselectedUsers([]);
        setSelectedUsers(users);
        setUnselectedUsers([]);
        setPreselectedUsers([]);
    }, [users]);

    const unselectAllUsers = useCallback(() => {
        setPreselectedUsers([]);
        setSelectedUsers([]);
        setUnselectedUsers(users);
        setPreselectedUsers([]);
    }, [users]);

    const unselectUsers = useCallback(() => {
        setPreunselectedUsers((currentPreunselectedUsers) => {
            setUnselectedUsers((previousList) => {
                const result = [...previousList, ...currentPreunselectedUsers];

                setSelectedUsers(users.filter((user) => !result.includes(user)));

                return result;
            });

            return [];
        });
    }, [users]);

    const onSubmitForm = useCallback(async (data) => {
        const payload = { ...data };
        delete payload.name;
        delete payload.toastType;
        delete payload.navigateTo;

        if (saveAsTemplate) {
            const response = await NotificationTemplate.createNotificationTemplate(
                id,
                {
                    name: data.name,
                    toastType: data.toastType,
                    navigateTo: data.navigateTo,
                    payload
                }
            );
            if (!response.error) {
                PubSub.publish('refresh-notifications', { newEntryId: response.data._id });
                if (!keepOpen) {
                    onClose();
                }
            }
            setSaveAsTemplate(false);
        }
        else {
            setIsLoading(true);
            const response = await sendNotification({
                deploymentId: id,
                templateId: objectSubTypeId,
                userIds: selectedUsers,
                body: {
                    payload: {
                        ...payload,
                        data: {
                            ...payload.data,
                            toastType: data.toastType,
                            navigateTo: data.navigateTo
                        }
                    }
                }
            });
            setIsLoading(false);

            if (!response.error) {
                const infoBody = `${translate("OTM_NOTIFICATIONS_SENT_SUCCESS")} (${response.data.successCount} ${translate("OTM_SUCCESS")}, ${response.data.failureCount} ${translate("OTM_FAILED")})`;
                PubSub.publish("show_info", infoBody, translate("OTM_INFO"));
            }

            if (!keepOpen) {
                onClose();
            }
        }
    }, [saveAsTemplate, id, objectSubTypeId, selectedUsers, keepOpen, onClose, translate]);

    const selectUserButtons = useMemo(() => {
        return (
            <div className='d-flex flex-column justify-content-center h-100'>
                <Button
                    variant='primary'
                    size='sm'
                    disabled={preselectedUsers.length === 0}
                    onClick={selectUsers}
                    className="mt-2"
                >
                    <Icon icon='ChevronRight' />
                </Button>
                <Button
                    variant='primary'
                    size='sm'
                    onClick={selectAllUsers}
                    disabled={unselectedUsers.length === 0}
                    className="mt-2"
                >
                    <Icon icon='DoubleArrow' />
                </Button>
                <Button
                    variant='primary'
                    size='sm'
                    onClick={unselectAllUsers}
                    disabled={selectedUsers.length === 0}
                    className="mt-2"
                >
                    <Icon icon='DoubleArrow' style={{ transform: "rotate(180deg)" }} />
                </Button>
                <Button
                    variant='primary'
                    size='sm'
                    disabled={preunselectedUsers.length === 0}
                    onClick={unselectUsers}
                    className="mt-2"
                >
                    <Icon icon='ChevronLeft' />
                </Button>
            </div>
        );
    }, [preselectedUsers, preunselectedUsers, selectedUsers.length, unselectedUsers.length, unselectAllUsers, selectUsers, selectAllUsers, unselectUsers]);

    const sendToUsersSection = useMemo(() => {
        return (
            <div className='row fs-7' style={{ height: '420px' }}>
                <div className='col h-100 overflow-auto'>
                    <div className="sticky-top mb-2">
                        <SearchBar
                            ref={ref}
                            size="sm"
                            initialValue={unselectedUsersSearchPrompt}
                            placeholder={translate("OTM_SEARCH")}
                            onChange={(value) => setUnselectedUsersSearchPrompt(value)} />
                    </div>
                    {users.length !== 0 ?
                        <List
                            data={unselectedUsers.filter(userId => userId.toLowerCase().includes(unselectedUsersSearchPrompt.toLowerCase())).map((userId) => ({ _id: userId, userId }))}
                            attr='userId'
                            multiple={true}
                            selectedElement={preselectedUsers}
                            onClick={(eventKey) => setPreselectedUsers((previousList) => updateUserList(previousList, eventKey))}
                        /> :
                        <label className="mt-2 ms-2">{translate("OTM_NO_DATA_FOUND")}</label>}
                </div>
                <div className='col-1'>
                    {selectUserButtons}
                </div>
                <div className='col h-100 overflow-auto'>
                    <div className="sticky-top mb-2">
                        <SearchBar
                            ref={ref}
                            size="sm"
                            initialValue={selectedUsersSearchPrompt}
                            placeholder={translate("OTM_SEARCH")}
                            onChange={(value) => setSelectedUsersSearchPrompt(value)}
                        />
                    </div>
                    <List
                        data={selectedUsers.filter(userId => userId.toLowerCase().includes(selectedUsersSearchPrompt.toLowerCase())).map((userId) => ({ _id: userId, userId }))}
                        attr='userId'
                        multiple={true}
                        selectedElement={preunselectedUsers}
                        onClick={(eventKey) => setPreunselectedUsers((previousList) => updateUserList(previousList, eventKey))}
                    />
                </div>
            </div>
        );
    }, [selectUserButtons, preselectedUsers, preunselectedUsers, selectedUsers, unselectedUsers, users, selectedUsersSearchPrompt, unselectedUsersSearchPrompt, translate, updateUserList]);

    return (
        <BootstrapModal show={show} size='xl' backdrop="static">
            <Formik initialValues={{
                name: templateNotification.name || '',
                toastType: templateNotification.toastType || '',
                navigateTo: templateNotification.navigateTo || '',
                title: templateNotification.title || '',
                badge: templateNotification.badge || '',
                body: templateNotification.body || '',
                icon: templateNotification.icon || '',
                image: templateNotification.image || '',
                renotify: templateNotification.renotify || false,
                silent: templateNotification.silent || false,
                requireInteraction: templateNotification.requireInteraction || false
            }}
                onSubmit={onSubmitForm}
                enableReinitialize>
                <Form>
                    <BootstrapModal.Header className="h5 text-secondary">{translate("OTM_NOTIFICATIONS_SEND")}</BootstrapModal.Header>
                    <BootstrapModal.Body style={{ overflowY: 'auto' }}>
                        <Accordion defaultActiveKey="0">
                            <Accordion.Item eventKey="0">
                                <Accordion.Header>
                                    <span className="h6">
                                        {translate("OTM_NOTIFICATIONS_PAYLOAD")}
                                    </span>
                                </Accordion.Header>
                                <Accordion.Body>
                                    <NotificationTemplateLayout
                                        disabled={!templatePermissions.update} />
                                </Accordion.Body>
                            </Accordion.Item>
                            <Accordion.Item eventKey="1">
                                <Accordion.Header>
                                    <span className="h6">
                                        {translate("OTM_NOTIFICATIONS_USERS_SELECTION")}
                                    </span>
                                </Accordion.Header>
                                <Accordion.Body>
                                    {sendToUsersSection}
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                    </BootstrapModal.Body>
                    <BootstrapModal.Footer>
                        <GenericCheckBox
                            css={{ toggle: "me-auto" }}
                            label={translate("OTM_KEEP_MODAL_OPEN")}
                            initialValue={keepOpen}
                            onChange={() => setKeepOpen(prev => !prev)}
                        />
                        <Button
                            disabled={!templatePermissions.create || isLoading}
                            type='submit'
                            onClick={() => { setSaveAsTemplate(true) }}
                            className="btn-sm">
                            {translate("OTM_NOTIFICATIONS_SAVE_AS_TEMPLATE")}
                        </Button>
                        <Button
                            variant='primary'
                            type='submit'
                            size='sm'
                            disabled={selectedUsers.length === 0 || !permissions.create || isLoading}
                        >
                            {translate('OTM_NOTIFICATIONS_SEND_TO_USER')}
                        </Button>
                        <Button onClick={onClose} variant="secondary" className="btn-sm">{translate("OTM_CLOSE")}</Button>
                    </BootstrapModal.Footer>
                </Form>
            </Formik>
        </BootstrapModal>
    );
};

export default SendNotificationModal;
